import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Toast {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  progress: number;
}

const MAX_TOASTS = 3;
const DEFAULT_DURATION = 4000;

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  private nextId = 0;
  private timers = new Map<number, ReturnType<typeof setInterval>>();

  /**
   * Observable du flux de toasts actifs
   */
  get toasts$(): Observable<Toast[]> {
    return this.toastsSubject.asObservable();
  }

  /**
   * Affiche un toast de notification
   */
  show(
    type: 'success' | 'error' | 'info' | 'warning',
    title: string,
    message: string,
    duration: number = DEFAULT_DURATION
  ): number {
    const id = this.nextId++;
    const toast: Toast = { id, type, title, message, progress: 100 };

    const currentToasts = this.toastsSubject.value;

    // Limiter la file a MAX_TOASTS - retirer le plus ancien si necessaire
    if (currentToasts.length >= MAX_TOASTS) {
      const oldest = currentToasts[0];
      this.dismiss(oldest.id);
    }

    this.toastsSubject.next([...this.toastsSubject.value, toast]);

    // Demarrer le timer de progression et d'auto-dismiss
    if (duration > 0) {
      const intervalStep = 50;
      const decrementPerStep = (100 / duration) * intervalStep;

      const intervalId = setInterval(() => {
        const toasts = this.toastsSubject.value;
        const toastIndex = toasts.findIndex(t => t.id === id);

        if (toastIndex === -1) {
          clearInterval(intervalId);
          this.timers.delete(id);
          return;
        }

        const updatedProgress = Math.max(0, toasts[toastIndex].progress - decrementPerStep);
        const updatedToasts = [...toasts];
        updatedToasts[toastIndex] = { ...updatedToasts[toastIndex], progress: updatedProgress };
        this.toastsSubject.next(updatedToasts);

        if (updatedProgress <= 0) {
          clearInterval(intervalId);
          this.timers.delete(id);
          this.dismiss(id);
        }
      }, intervalStep);

      this.timers.set(id, intervalId);
    }

    return id;
  }

  /**
   * Ferme un toast par son identifiant
   */
  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearInterval(timer);
      this.timers.delete(id);
    }

    const toasts = this.toastsSubject.value.filter(t => t.id !== id);
    this.toastsSubject.next(toasts);
  }

  /**
   * Ferme tous les toasts actifs
   */
  dismissAll(): void {
    this.timers.forEach(timer => clearInterval(timer));
    this.timers.clear();
    this.toastsSubject.next([]);
  }
}
